// src/components/ui-components/RoomForm.jsx

import { Badge, Box, Button, Dialog, Flex, IconButton, Switch, Text, TextField, DropdownMenu } from '@radix-ui/themes';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify'; // *** MODIFICATION: Use react-toastify ***
import { FaBed, FaEllipsisV, FaTimes } from 'react-icons/fa';
import axiosInstance from '../../config/axiosConfig';
import ConfirmationDialogue from './ConfirmationDialogue';
import ErrorPopup from './ErrorPopup';
import SearchableDropdown from './SearchableDropdown';

const RoomForm = ({ roleId, bedData, refreshBeds, onOpenChange }) => {
  const isDisabled = roleId === '2' || roleId === '4';
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAdmitDischargeConfirm, setShowAdmitDischargeConfirm] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [patientReferenceError, setPatientReferenceError] = useState('');

  // Section state management
  const [section1Complete, setSection1Complete] = useState(false);
  const [section2Complete, setSection2Complete] = useState(false);

  const [formData, setFormData] = useState({
    token: bedData?.token || '',
    userToken: bedData?.userToken || null,
    roomId: bedData?.roomId || 0,
    id: bedData?.id || 0,
    roomNumber: bedData?.roomNumber || '',
    bedNumber: bedData?.bedName || '',
    bedName: bedData?.bedName || '',
    gatewayId: bedData?.gatewayId?.toString() || '',
    sensorId: bedData?.sensorId?.toString() || '',
    isWetSafeEnabled: bedData?.isWetSafeEnabled || null,
    isRollingEnabled: bedData?.isRollingEnabled || null,
    isWakeupEnabled: bedData?.isWakeupEnabled || null,
    isNurseCallEnabled: bedData?.isNurseCallEnabled || null,
    isPatientOutEnabled: bedData?.isPatientOutEnabled || null,
    isFoodCallEnabled: bedData?.isFoodCallEnabled || null,
    isSecurityCallEnabled: bedData?.isSecurityCallEnabled || null,
    isLaunderyCallEnabled: bedData?.isLaunderyCallEnabled || null,
    isConfirmed: bedData?.isConfirmed || null,
    isHold: bedData?.isHold || null,
    isActive: bedData?.isActive || null,
    createdOn: bedData?.createdOn || null,
    lastUpdOn: bedData?.lastUpdOn || null,
    patientsReferenceNumber: bedData?.patientsReferenceNumber || '',
    patientToken: bedData?.patientToken || null,
    description: bedData?.description || '',
    isDischarge: bedData?.isDischarge || false,
    isAdmit: bedData?.isAdmit || false,
    isSecurity: bedData?.isSecurity || true,
    isHousekeeping: bedData?.isHousekeeping || true,
    isFnb: bedData?.isFnb || false,
  });

  // Initialize section states based on existing data
  useEffect(() => {
    setSection1Complete(Boolean(bedData?.gatewayId));
    setSection2Complete(Boolean(bedData?.patientToken && bedData?.patientsReferenceNumber));
  }, [bedData]);

  useEffect(() => {
    if (!isOpen || !bedData) return;

    setFormData((prev) => ({
      ...prev,
      token: bedData?.token || prev.token,
      userToken: bedData?.userToken || prev.userToken,
      roomId: bedData?.roomId || prev.roomId,
      id: bedData?.id || prev.id,
      roomNumber: bedData?.roomNumber || prev.roomNumber,
      bedNumber: bedData?.bedName || prev.bedNumber,
      bedName: bedData?.bedName || prev.bedName,
      gatewayId: bedData?.gatewayId?.toString() || prev.gatewayId,
      sensorId: bedData?.sensorId?.toString() || '',
      isWetSafeEnabled: bedData?.isWetSafeEnabled ?? prev.isWetSafeEnabled,
      isRollingEnabled: bedData?.isRollingEnabled ?? prev.isRollingEnabled,
      isWakeupEnabled: bedData?.isWakeupEnabled ?? prev.isWakeupEnabled,
      isNurseCallEnabled: bedData?.isNurseCallEnabled ?? prev.isNurseCallEnabled,
      isPatientOutEnabled: bedData?.isPatientOutEnabled ?? prev.isPatientOutEnabled,
      isFoodCallEnabled: bedData?.isFoodCallEnabled ?? prev.isFoodCallEnabled,
      isSecurityCallEnabled: bedData?.isSecurityCallEnabled ?? prev.isSecurityCallEnabled,
      isLaunderyCallEnabled: bedData?.isLaunderyCallEnabled ?? prev.isLaunderyCallEnabled,
      patientToken: bedData?.patientToken || null,
      patientsReferenceNumber: bedData?.patientToken ? (bedData?.patientsReferenceNumber || prev.patientsReferenceNumber) : '',
      description: bedData?.patientToken ? (bedData?.description || prev.description) : '',
      isFnb: bedData?.isFnb ?? prev.isFnb,
      isSecurity: bedData?.isSecurity ?? prev.isSecurity,
      isHousekeeping: bedData?.isHousekeeping ?? prev.isHousekeeping,
    }));
    setSection1Complete(Boolean(bedData?.gatewayId));
    setSection2Complete(Boolean(bedData?.patientToken && bedData?.patientsReferenceNumber));
  }, [bedData, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchGateways = async () => {
      try {
        const params = formData.id ? { bedId: formData.id } : {};
        const response = await axiosInstance.get('/Gateway/get-institution-gateway-dropdowns', { params });
        const gatewayList = response.data.result || [];
        setGateways(gatewayList);
      } catch (error) {
        console.error('Error fetching gateways:', error);
        toast.error('Failed to load gateways');
      }
    };

    const fetchSensors = async () => {
      try {
        const params = formData.id ? { roomId: formData.id } : {};
        const response = await axiosInstance.get('/Sensor/get-institution-sensor-dropdowns', { params });
        const sensorList = response.data.result || [];
        setSensors(sensorList);
      } catch (error) {
        console.error('Error fetching sensors:', error);
        toast.error('Failed to load sensors');
      }
    };

    fetchGateways();
    fetchSensors();
  }, [isOpen, formData.roomId, formData.id]);

  const handleInputChange = (field, value) => {
    if (field === 'patientsReferenceNumber' && value.trim()) {
      setPatientReferenceError('');
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const refreshBedList = async () => {
    if (refreshBeds) {
      await refreshBeds();
    }
  };

  const validatePatientReference = () => {
    if (!formData.patientsReferenceNumber?.trim()) {
      const message = 'Patient reference number is required';
      setPatientReferenceError(message);
      toast.warn(message);
      return false;
    }

    setPatientReferenceError('');
    return true;
  };

  const openDialog = () => {
    setIsOpen(true);
    onOpenChange && onOpenChange(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    onOpenChange && onOpenChange(false);
  };

  // Section-specific save handlers
  const handleSaveSection1 = async () => {
    if (!formData.gatewayId) {
      toast.warn('Gateway is required');
      return;
    }

    const payload = {
      token: formData.token,
      userToken: formData.userToken,
      roomId: formData.roomId,
      id: formData.id,
      roomNumber: formData.roomNumber,
      bedNumber: formData.bedNumber,
      bedName: formData.bedName,
      gatewayId: parseInt(formData.gatewayId),
      sensorId: formData.sensorId ? parseInt(formData.sensorId) : null,
      isWetSafeEnabled: formData.isWetSafeEnabled,
      isRollingEnabled: formData.isRollingEnabled,
      isWakeupEnabled: formData.isWakeupEnabled,
      isNurseCallEnabled: formData.isNurseCallEnabled,
      isPatientOutEnabled: formData.isPatientOutEnabled,
      isFoodCallEnabled: formData.isFoodCallEnabled,
      isSecurityCallEnabled: formData.isSecurityCallEnabled,
      isLaunderyCallEnabled: formData.isLaunderyCallEnabled,
      isConfirmed: formData.isConfirmed,
      isHold: formData.isHold,
      isActive: formData.isActive,
      createdOn: formData.createdOn,
      lastUpdOn: formData.lastUpdOn,
      patientsReferenceNumber: formData.patientsReferenceNumber,
      patientToken: formData.patientToken,
      description: formData.description,
      isDischarge: formData.isDischarge,
      isAdmit: formData.isAdmit,
      isSensorNeeded: false,
      isSecurity: formData.isSecurity,
      isHousekeeping: formData.isHousekeeping,
      isFnb: formData.isFnb,
    };

    try {
      const endpoint = formData.id ? '/Bed/update-bed' : '/Bed/add-bed';
      const response = await axiosInstance.post(endpoint, payload);

      if (response.data.result) {
        setSection1Complete(true);
        toast.success('Bed settings saved successfully');
        await refreshBedList();
      } else {
        toast.error(response.data.errorResponse.message || 'Failed to save bed settings');
      }
    } catch (error) {
      toast.error('An error occurred while saving bed settings');
      console.error(error);
    }
  };

  const handleSaveSection2 = async () => {
    if (!validatePatientReference()) {
      return;
    }

    const payload = {
      token: formData.token,
      userToken: formData.userToken,
      roomId: formData.roomId,
      id: formData.id,
      roomNumber: formData.roomNumber,
      bedNumber: formData.bedNumber,
      bedName: formData.bedName,
      gatewayId: parseInt(formData.gatewayId),
      sensorId: formData.sensorId ? parseInt(formData.sensorId) : null,
      description: formData.description,
      isWetSafeEnabled: formData.isWetSafeEnabled,
      isRollingEnabled: formData.isRollingEnabled,
      isWakeupEnabled: formData.isWakeupEnabled,
      isNurseCallEnabled: formData.isNurseCallEnabled,
      isPatientOutEnabled: formData.isPatientOutEnabled,
      isFoodCallEnabled: formData.isFoodCallEnabled,
      isSecurityCallEnabled: formData.isSecurityCallEnabled,
      isLaunderyCallEnabled: formData.isLaunderyCallEnabled,
      isConfirmed: formData.isConfirmed,
      isHold: formData.isHold,
      isActive: formData.isActive,
      createdOn: formData.createdOn,
      lastUpdOn: formData.lastUpdOn,
      patientsReferenceNumber: formData.patientsReferenceNumber,
      patientToken: formData.patientToken,
      description: formData.description,
      isDischarge: formData.isDischarge,
      isAdmit: formData.isAdmit,
      isSensorNeeded: false,
      isSecurity: formData.isSecurity,
      isHousekeeping: formData.isHousekeeping,
      isFnb: formData.isFnb,
    };

    try {
      const response = await axiosInstance.post('/Bed/update-bed', payload);

      if (response.data.result) {
        setSection2Complete(true);
        toast.success('Patient details updated successfully');
        await refreshBedList();
      } else {
        toast.error(response.data.errorResponse.message || 'Failed to update patient details');
      }
    } catch (error) {
      toast.error('An error occurred while updating patient details');
      console.error(error);
    }
  };

  const handleSaveSection3 = async () => {
    const payload = {
      token: formData.token,
      userToken: formData.userToken,
      roomId: formData.roomId,
      id: formData.id,
      roomNumber: formData.roomNumber,
      bedNumber: formData.bedNumber,
      bedName: formData.bedName,
      gatewayId: parseInt(formData.gatewayId),
      sensorId: formData.sensorId ? parseInt(formData.sensorId) : null,
      isWetSafeEnabled: formData.isWetSafeEnabled,
      isRollingEnabled: formData.isRollingEnabled,
      isWakeupEnabled: formData.isWakeupEnabled,
      isNurseCallEnabled: formData.isNurseCallEnabled,
      isPatientOutEnabled: formData.isPatientOutEnabled,
      isFoodCallEnabled: formData.isFoodCallEnabled,
      isSecurityCallEnabled: formData.isSecurityCallEnabled,
      isLaunderyCallEnabled: formData.isLaunderyCallEnabled,
      isConfirmed: formData.isConfirmed,
      isHold: formData.isHold,
      isActive: formData.isActive,
      createdOn: formData.createdOn,
      lastUpdOn: formData.lastUpdOn,
      patientsReferenceNumber: formData.patientsReferenceNumber,
      patientToken: formData.patientToken,
      description: formData.description,
      isDischarge: formData.isDischarge,
      isAdmit: formData.isAdmit,
      isSensorNeeded: false,
      isSecurity: formData.isSecurity,
      isHousekeeping: formData.isHousekeeping,
      isFnb: formData.isFnb,
    };

    try {
      const response = await axiosInstance.post('/Bed/update-bed', payload);

      if (response.data.result) {
        toast.success('Alarm settings saved successfully');
        await refreshBedList();
      } else {
        toast.error(response.data.errorResponse.message || 'Failed to save alarm settings');
      }
    } catch (error) {
      toast.error('An error occurred while saving alarm settings');
      console.error(error);
    }
  };

  const handleAddBed = async () => {
    const payload = {
      token: formData.token,
      roomId: formData.roomId,
      bedNumber: formData.bedNumber,
      bedName: formData.bedName,
      gatewayId: parseInt(formData.gatewayId),
      sensorId: formData.sensorId ? parseInt(formData.sensorId) : null,
      isSensorNeeded: false,
    };
    try {
      const response = await axiosInstance.post('/Bed/add-bed', payload);
      if (response.data.result) {
        toast.success('Bed added to room successfully');
        await refreshBedList();
      } else {
        const message = response.data.errorResponse?.message || 'Failed to add bed';
        setErrorMessage(message);
        setIsErrorOpen(true);
      }
    } catch (error) {
      const message = error.response?.data?.errorResponse?.message || 'An error occurred while adding the bed';
      setErrorMessage(message);
      setIsErrorOpen(true);
      console.error(error);
    }
  };

  const handleAdmitDischarge = async () => {
    const isAdmitAction = !formData.patientToken;

    if (isAdmitAction && !validatePatientReference()) {
      return;
    }

    const payload = {
      token: formData.token,
      id: formData.id,
      patientToken: formData.patientToken,
      patientsReferenceNumber: formData.patientsReferenceNumber,
      description: formData.description,
      isAdmit: isAdmitAction,
      isDischarge: !isAdmitAction,
      isSensorNeeded: false,
    };

    try {
      const response = await axiosInstance.post('/Bed/update-bed', payload);
      if (response.data.result) {
        toast.success(`Patient ${isAdmitAction ? 'admitted' : 'discharged'} successfully`);
        setSection2Complete(isAdmitAction);
        setFormData((prev) => ({
          ...prev,
          patientToken: isAdmitAction ? prev.patientToken : null,
          patientsReferenceNumber: isAdmitAction ? prev.patientsReferenceNumber : '',
          description: isAdmitAction ? prev.description : '',
        }));
        await refreshBedList();
      } else {
        toast.error(response.data.errorResponse.message || `Failed to ${isAdmitAction ? 'admit' : 'discharge'} patient`);
      }
    } catch (error) {
      toast.error(`An error occurred while ${isAdmitAction ? 'admitting' : 'discharging'} the patient`);
      console.error(error);
    }
  };

  // --- No changes to the functions below ---

  const handleConfirmAddBed = () => {
    setShowConfirm(false);
    handleAddBed();
  };

  const handleConfirmAdmitDischarge = () => {
    setShowAdmitDischargeConfirm(false);
    handleAdmitDischarge();
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setShowAdmitDischargeConfirm(false);
  };

  // Filter alarms based on sensorId
  const alarms = [
    ...(formData.sensorId ? [
      { label: 'Wet-Safe', key: 'isWetSafeEnabled' },
      { label: 'Wake up', key: 'isWakeupEnabled' },
      { label: 'Rolling', key: 'isRollingEnabled' },
    ] : []),
    { label: 'Nurse Call', key: 'isNurseCallEnabled' },
    { label: 'Out Of Room', key: 'isPatientOutEnabled' },
    ...(formData.isFnb ? [{ label: 'F&B', key: 'isFoodCallEnabled' }] : []),
    ...(formData.isSecurity ? [{ label: 'Security', key: 'isSecurityCallEnabled' }] : []),
    ...(formData.isHousekeeping ? [{ label: 'Housekeeping', key: 'isLaunderyCallEnabled' }] : []),
  ];

  // Dialog content to be portaled
  const dialogContent = isOpen ? (
    <>
      <Dialog.Content
        maxWidth="720px"
        className="room-management-dialog"
        style={{ padding: 0, overflow: 'hidden' }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <style>
          {`
            .room-management-dialog {
              border-radius: 14px;
              box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
            }
            .room-management-section {
              border: 1px solid #dbe7e5;
              border-radius: 10px;
              background: #ffffff;
              padding: 16px;
            }
            .room-management-section-disabled {
              background: #f8fafc;
            }
          `}
        </style>

        <Box className="bg-[#ecf2f1] px-6 py-5">
          <Flex align="start" justify="between" gap="4">
            <Box>
              <Dialog.Title>
                <Text size="6" weight="bold" style={{ color: '#0b5a97' }}>
                  Room Management
                </Text>
              </Dialog.Title>
              <Dialog.Description>
                <Text size="2" color="gray">
                  Room {formData.roomNumber || '-'} / {formData.bedName || formData.bedNumber || 'Bed'}
                </Text>
              </Dialog.Description>
            </Box>
            <IconButton
              variant="ghost"
              size="2"
              color="gray"
              onClick={closeDialog}
              aria-label="Close room management"
            >
              <FaTimes />
            </IconButton>
          </Flex>
        </Box>

        <Box className="max-h-[75vh] overflow-y-auto bg-[#f8fbfb] px-6 py-5">
          <Flex direction="column" gap="4">
            {/* Section 1: Bed Management */}
            <Box className="room-management-section">
              <Flex align="center" justify="between" gap="3" mb="4">
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Text as="div" size="3" weight="bold" style={{ color: '#0b5a97' }}>
                      Bed Settings
                    </Text>
                    <Badge color={section1Complete ? 'green' : 'gray'} variant="soft">
                      {section1Complete ? 'Configured' : 'Required'}
                    </Badge>
                  </Flex>
                  <Text size="1" color="gray">
                    Assign the bed, gateway, and sensor used by this room.
                  </Text>
                </Flex>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger>
                    <IconButton variant="soft" size="2" color="gray">
                      <FaEllipsisV />
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    <DropdownMenu.Item onSelect={() => setShowConfirm(true)} disabled={isDisabled}>
                      Add Bed
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </Flex>

              <Flex gap="3" mb="3" align="center">
                <label className="w-full">
                  <Text as="div" size="2" mb="1" weight="medium">
                    Bed Number
                  </Text>
                  <TextField.Root
                    value={formData.bedName}
                    onChange={(e) => handleInputChange('bedName', e.target.value)}
                    placeholder="Bed Number"
                    disabled={isDisabled}
                  />
                </label>
              </Flex>

              <Flex gap="3" wrap="wrap">
                <label className="min-w-[220px] flex-1">
                  <Text as="div" size="2" mb="1" weight="medium">
                    Gateway *
                  </Text>
                  <SearchableDropdown
                    items={gateways}
                    placeholder="Select gateway"
                    onSelect={(item) => handleInputChange('gatewayId', item?.id?.toString())}
                    renderItem={(item) => item?.igin || ''}
                    defaultValue={formData.gatewayId}
                    disabled={isDisabled}
                  />
                </label>

                <label className="min-w-[220px] flex-1">
                  <Text as="div" size="2" mb="1" weight="medium">
                    Sensor
                  </Text>
                  <SearchableDropdown
                    items={sensors}
                    placeholder="Select sensor"
                    onSelect={(item) => handleInputChange('sensorId', item?.id?.toString())}
                    renderItem={(item) => item?.isin || ''}
                    defaultValue={formData.sensorId}
                    disabled={isDisabled || !formData.gatewayId}
                  />
                </label>
              </Flex>

              <Flex mt="4" justify="end">
                <Button
                  onClick={handleSaveSection1}
                  disabled={isDisabled || !formData.gatewayId}
                  style={{ backgroundColor: '#0b5a97' }}
                >
                  Save Bed Settings
                </Button>
              </Flex>
            </Box>

            {/* Section 2: Patient Details */}
            <Box className={`room-management-section ${!section1Complete ? 'room-management-section-disabled' : ''}`}>
              <Flex align="center" justify="between" gap="3" mb="4">
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Text as="div" size="3" weight="bold" style={{ color: '#0b5a97' }}>
                      Patient Details
                    </Text>
                    <Badge color={section2Complete ? 'green' : 'gray'} variant="soft">
                      {section2Complete ? 'Complete' : 'PRN required'}
                    </Badge>
                  </Flex>
                  <Text size="1" color="gray">
                    Patient reference number is required before alarm settings can be changed.
                  </Text>
                </Flex>
              </Flex>

              <Flex direction="column" gap="3">
                <label className="w-full">
                  <Text as="div" size="2" mb="1" weight="medium">
                    Patient Reference Number *
                  </Text>
                  <TextField.Root
                    value={formData.patientsReferenceNumber}
                    onChange={(e) => handleInputChange('patientsReferenceNumber', e.target.value)}
                    placeholder="Enter patient reference number"
                    disabled={isDisabled || !section1Complete}
                    aria-invalid={Boolean(patientReferenceError)}
                  />
                  {patientReferenceError && (
                    <Text as="div" size="1" color="red" mt="1">
                      {patientReferenceError}
                    </Text>
                  )}
                </label>

                <label className="w-full">
                  <Text as="div" size="2" mb="1" weight="medium">
                    Description
                  </Text>
                  <TextField.Root
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter description"
                    disabled={isDisabled || !section1Complete}
                  />
                </label>
              </Flex>

              <Flex mt="4" gap="3" justify="end" wrap="wrap">
                <Button
                  onClick={() => {
                    if (!formData.patientToken && !validatePatientReference()) return;
                    setShowAdmitDischargeConfirm(true);
                  }}
                  disabled={isDisabled || !section1Complete}
                  color={formData.patientToken ? 'red' : 'green'}
                  variant={formData.patientToken ? 'soft' : 'solid'}
                >
                  {formData.patientToken ? 'Discharge' : 'Admit'}
                </Button>
                {formData.patientToken && (
                  <Button
                    onClick={handleSaveSection2}
                    disabled={isDisabled || !section1Complete}
                    style={{ backgroundColor: '#0b5a97' }}
                  >
                    Update Patient
                  </Button>
                )}
              </Flex>
            </Box>

            {/* Section 3: Alarms */}
            <Box className={`room-management-section ${(!section1Complete || !section2Complete) ? 'room-management-section-disabled' : ''}`}>
              <Flex align="center" justify="between" gap="3" mb="4">
                <Flex direction="column" gap="1">
                  <Text as="div" size="3" weight="bold" style={{ color: '#0b5a97' }}>
                    Alarm Settings
                  </Text>
                  <Text size="1" color="gray">
                    Enable the alarm types available for this bed.
                  </Text>
                </Flex>
              </Flex>

              <Flex gap="3" wrap="wrap">
                {alarms.map((alarm, index) => {
                  const isSwitchDisabled = isDisabled || !section1Complete || !section2Complete;
                  const isChecked = formData[alarm.key];

                  return (
                    <Text
                      as="label"
                      size="2"
                      key={index}
                      className="rounded-md border border-[#e2ecea] bg-white px-3 py-2"
                      style={{
                        cursor: isSwitchDisabled ? 'not-allowed' : 'pointer',
                        color: isSwitchDisabled ? (isChecked ? '#16a34a' : '#6b7280') : 'inherit',
                        opacity: isSwitchDisabled ? 0.75 : 1
                      }}
                    >
                      <Flex gap="2" align="center">
                        <Switch
                          size="1"
                          checked={isChecked}
                          onCheckedChange={(checked) => handleInputChange(alarm.key, checked)}
                          disabled={isSwitchDisabled}
                        />
                        {alarm.label}
                      </Flex>
                    </Text>
                  );
                })}
              </Flex>

              <Flex mt="4" justify="end">
                <Button
                  onClick={handleSaveSection3}
                  disabled={isDisabled || !section1Complete || !section2Complete}
                  style={{ backgroundColor: '#0b5a97' }}
                >
                  Save Alarm Settings
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Box>

        {/* Confirmation Dialogues */}
        {showConfirm && (
          <ConfirmationDialogue
            Name={'bed to this room'}
            Type="to Add Bed"
            Action="add"
            onCancel={handleCancelConfirm}
            onConfirm={handleConfirmAddBed}
          />
        )}
        {showAdmitDischargeConfirm && (
          <ConfirmationDialogue
            Name={formData.bedName || 'this bed'}
            Type={formData.patientToken ? 'to Discharge Bed' : 'to Admit Bed'}
            Action={formData.patientToken ? 'discharge' : 'admit'}
            onCancel={handleCancelConfirm}
            onConfirm={handleConfirmAdmitDischarge}
          />
        )}
      </Dialog.Content>
    </>
  ) : null;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (open) openDialog();
      }}
    >
      <Dialog.Trigger>
        <IconButton color="jade" size="3" variant="soft">
          <Flex align="center" direction="column">
            <FaBed width="18" height="18" />
            <Text size="1" className="font-medium">{bedData?.bedNumber}</Text>
          </Flex>
        </IconButton>
      </Dialog.Trigger>
      {isOpen && createPortal(dialogContent, document.body)}
      <ErrorPopup
        errorMessage={errorMessage}
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
      />
    </Dialog.Root>
  );
};

export default RoomForm;
